import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckIcon from '@mui/icons-material/Check';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { FC, useState } from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { Document, Page, pdfjs } from 'react-pdf';
import { createGlobalStyle } from 'styled-components';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import Flex from 'quarks/Flex';
import type { BasicProps } from 'quarks/interpolations/basic';

import type { IComponentPdfRecord } from 'graphql/types';

interface PdfProps extends BasicProps, IComponentPdfRecord {
  internalName?: IComponentPdfRecord['internalName'];
}

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const ComponentPdf: FC<PdfProps> = props => {
  const { pdf } = props;

  const [numPages, setNumPages] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [copiedMessageVisible, setCopiedMessageVisible] = useState(false);

  const handle = useFullScreenHandle();

  const onDocumentLoadSuccess = ({ numPages: loadedNumPages }: { numPages: number }) => {
    setNumPages(loadedNumPages);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (numPages && currentPage < numPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const enterFullScreen = () => {
    if (!handle.active) {
      handle.enter();
    }
  };

  const copyPdfUrlToClipboard = () => {
    if (pdf && pdf.url) {
      navigator.clipboard.writeText(pdf.url).then(() => {
        setCopiedMessageVisible(true);
        setTimeout(() => {
          setCopiedMessageVisible(false);
        }, 1000);
      });
    }
  };

  const DocumentStyle = createGlobalStyle`
    .react-pdf__Page__canvas {
      width: 100% !important;
      height: auto !important;
    }
  `;

  return (
    <Flex
      flexDirection="column"
      textAlign="center"
      alignItems="center"
      marginX="auto"
      marginBottom={24}
      backgroundColor="foam-50"
      width="100%"
      minHeight={handle.active ? '100vh' : 'auto'}
    >
      <FullScreen handle={handle}>
        <Flex
          alignItems="center"
          height={handle.active ? '100vh' : 'auto'}
          justifyContent="center"
          flexDirection="column"
          textAlign="center"
          marginX="auto"
        >
          <DocumentStyle />
          <Document file={pdf?.url} loading={<CircularProgress />} onLoadSuccess={onDocumentLoadSuccess}>
            <Page key={`page_${currentPage}`} pageNumber={currentPage} scale={0.6} />
            {handle.active && (
              <Flex
                position="absolute"
                bottom="20px"
                left="50%"
                transform="translateX(-50%)"
                zIndex={2}
                boxShadow="0 30px 40px 0 rgba(16, 36, 94, .2)"
                borderRadius="5px"
                backgroundColor="common-white"
              >
                <Flex
                  cursor="pointer"
                  onClick={goToPreviousPage}
                  alignItems="center"
                  paddingAll={16}
                  backgroundColor="common-white"
                  borderBottomLeftRadius="5px"
                  borderTopLeftRadius="5px"
                  hover={{ backgroundColor: 'cloud-50' }}
                >
                  <ArrowBackIosIcon sx={{ fontSize: 20 }} />
                </Flex>
                <Flex paddingAll={16} backgroundColor="common-white" fontSize="textXs" sm={{ fontSize: 'textMd' }}>
                  {`${currentPage} of ${numPages}`}
                </Flex>
                <Flex
                  cursor="pointer"
                  onClick={goToNextPage}
                  alignItems="center"
                  paddingAll={16}
                  backgroundColor="common-white"
                  borderBottomRightRadius="5px"
                  borderTopRightRadius="5px"
                  hover={{ backgroundColor: 'cloud-50' }}
                >
                  <ArrowForwardIosIcon sx={{ fontSize: 20 }} />
                </Flex>
              </Flex>
            )}
          </Document>
        </Flex>
      </FullScreen>
      <Flex
        zIndex={2}
        backgroundColor="bubbles-50"
        justifyContent="space-between"
        width="100%"
        paddingX={16}
        paddingY={12}
      >
        <Flex alignItems="center">
          <Flex onClick={goToPreviousPage} cursor="pointer" marginRight={4}>
            <ArrowBackIosIcon sx={{ fontSize: 24 }} />
          </Flex>
          <Flex onClick={goToNextPage} cursor="pointer">
            <ArrowForwardIosIcon sx={{ fontSize: 24 }} />
          </Flex>
        </Flex>
        <Flex alignItems="center">
          {copiedMessageVisible && (
            <Flex fontSize="textXs" alignItems="center" paddingRight={8}>
              <CheckIcon sx={{ fontSize: 18 }} />
              <Flex marginLeft={4}>Copied!</Flex>
            </Flex>
          )}
          <Flex onClick={copyPdfUrlToClipboard} cursor="pointer" marginRight={8}>
            <ShareOutlinedIcon sx={{ fontSize: 24 }} />
          </Flex>
          <Flex onClick={enterFullScreen} cursor="pointer">
            <FullscreenIcon sx={{ fontSize: 32 }} />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default ComponentPdf;
